import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Link as MuiLink,
  List as MuiList,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from 'recharts';
import { registerReact } from '../registry/builtins';
import { passthroughMuiProps, mergeStyles } from './passthroughProps';

const CHART_COLORS = ['#1B4F72', '#148F77', '#B9770E', '#922B21', '#5B2C6F'];

/**
 * Builtin views forward unknown props to MUI so agents can use the full
 * @material-ui/core v4 surface (variant, style, className, classes, etc.).
 * Canvas-only keys are stripped via passthroughMuiProps.
 */

export function StackView({ props: p = {}, children }) {
  const direction = p.direction === 'row' ? 'row' : 'column';
  const spacing = p.spacing != null ? Number(p.spacing) * 8 : 16;
  const mui = passthroughMuiProps(p, {
    omit: ['direction', 'spacing'],
    defaults: { display: 'flex', flexDirection: direction },
    overrides: {
      style: mergeStyles(
        { gap: spacing, width: '100%' },
        p.style,
      ),
    },
  });
  return <Box {...mui}>{children}</Box>;
}

export function GridView({ props: p = {}, children }) {
  const cols = p.columns || { xs: 1, sm: 2, md: 3 };
  const spacing = p.spacing != null ? Number(p.spacing) : 2;
  const childArray = React.Children.toArray(children);
  const mdSpan = typeof cols === 'number' ? 12 / cols : 12 / (cols.md || cols.sm || cols.xs || 1);
  const containerProps = passthroughMuiProps(p, {
    omit: ['columns', 'spacing', 'itemProps'],
    defaults: { container: true, spacing },
  });
  const itemDefaults = p.itemProps && typeof p.itemProps === 'object' ? p.itemProps : {};
  return (
    <Grid {...containerProps}>
      {childArray.map((child, i) => (
        <Grid
          item
          key={child.key || i}
          xs={12 / (cols.xs || 1)}
          sm={12 / (cols.sm || cols.xs || 1)}
          md={mdSpan}
          {...itemDefaults}
        >
          {child}
        </Grid>
      ))}
    </Grid>
  );
}

export function BoxView({ props: p = {}, children }) {
  const mui = passthroughMuiProps(p);
  return <Box {...mui}>{children}</Box>;
}

export function DividerView({ props: p = {} }) {
  const mui = passthroughMuiProps(p, {
    defaults: { style: { margin: '12px 0' } },
    overrides: { style: mergeStyles({ margin: '12px 0' }, p.style) },
  });
  return <Divider {...mui} />;
}

export function SpacerView({ props: p = {} }) {
  const mui = passthroughMuiProps(p, {
    omit: ['height'],
    overrides: {
      style: mergeStyles({ height: p.height != null ? p.height : 16 }, p.style),
    },
  });
  return <Box {...mui} />;
}

export function TypographyView({ props: p = {} }) {
  // Canvas convenience: `text` → children. Agents may also set children via props.childText etc.
  const { text, children: propsChildren, ...rest } = p;
  const mui = passthroughMuiProps(rest, {
    defaults: { variant: 'body1' },
  });
  return <Typography {...mui}>{text != null ? text : propsChildren}</Typography>;
}

export function BannerView({ props: p = {} }) {
  const {
    text,
    TypographyProps,
    elevation = 0,
    ...rest
  } = p;
  const paper = passthroughMuiProps(rest, {
    omit: ['text', 'TypographyProps'],
    defaults: { elevation },
    overrides: {
      style: mergeStyles(
        {
          padding: 16,
          background: '#EBF5FB',
          borderLeft: '4px solid #1B4F72',
        },
        p.style,
      ),
    },
  });
  const typography = passthroughMuiProps(TypographyProps || {}, {
    defaults: { variant: 'body2' },
  });
  return (
    <Paper {...paper}>
      <Typography {...typography}>{text}</Typography>
    </Paper>
  );
}

export function MetricCardView({ props: p = {} }) {
  const {
    label,
    value,
    caption,
    accent,
    LabelProps,
    ValueProps,
    CaptionProps,
    ...rest
  } = p;
  const paper = passthroughMuiProps(rest, {
    omit: ['label', 'value', 'caption', 'accent', 'LabelProps', 'ValueProps', 'CaptionProps'],
    defaults: { elevation: 1 },
    overrides: {
      style: mergeStyles(
        {
          padding: 16,
          borderTop: accent ? '3px solid #922B21' : '3px solid #1B4F72',
          minHeight: 96,
        },
        p.style,
      ),
    },
  });
  return (
    <Paper {...paper}>
      <Typography
        {...passthroughMuiProps(LabelProps || {}, {
          defaults: { variant: 'caption', color: 'textSecondary' },
        })}
      >
        {label}
      </Typography>
      <Typography
        {...passthroughMuiProps(ValueProps || {}, {
          defaults: { variant: 'h5' },
          overrides: {
            style: mergeStyles({ fontWeight: 600, marginTop: 4 }, ValueProps && ValueProps.style),
          },
        })}
      >
        {value != null && value !== '' ? String(value) : '—'}
      </Typography>
      {caption ? (
        <Typography
          {...passthroughMuiProps(CaptionProps || {}, {
            defaults: { variant: 'caption', color: 'textSecondary' },
          })}
        >
          {caption}
        </Typography>
      ) : null}
    </Paper>
  );
}

export function ListView({ props: p = {}, onEvent }) {
  const { title, items, TitleProps, ListProps, ListItemProps, onItemClick, ...rest } = p;
  const listItems = Array.isArray(items) ? items : [];
  const paper = passthroughMuiProps(rest, {
    omit: ['title', 'items', 'TitleProps', 'ListProps', 'ListItemProps', 'onItemClick'],
    defaults: { elevation: 0 },
    overrides: { style: mergeStyles({ padding: 8 }, p.style) },
  });
  return (
    <Paper {...paper}>
      {title ? (
        <Typography
          {...passthroughMuiProps(TitleProps || {}, {
            defaults: { variant: 'h6' },
            overrides: {
              style: mergeStyles({ padding: '8px 16px' }, TitleProps && TitleProps.style),
            },
          })}
        >
          {title}
        </Typography>
      ) : null}
      <MuiList dense {...passthroughMuiProps(ListProps || {})}>
        {listItems.length === 0 ? (
          <ListItem>
            <ListItemText primary="No items" />
          </ListItem>
        ) : (
          listItems.map((item, i) => (
            <ListItem
              key={item.id || i}
              button={!!(onItemClick || onEvent)}
              onClick={() => {
                if (onItemClick) onItemClick(item);
                if (onEvent) onEvent('onItemClick', { item, row: item });
              }}
              {...passthroughMuiProps(ListItemProps || {})}
            >
              <ListItemText
                primary={item.title || item.label || String(item)}
                secondary={item.subtitle || item.value}
              />
            </ListItem>
          ))
        )}
      </MuiList>
    </Paper>
  );
}

export function DataTableView({ props: p = {}, onEvent }) {
  const { title, columns, rows, TitleProps, tableStyle, ...rest } = p;
  const cols = Array.isArray(columns) ? columns : [];
  const dataRows = Array.isArray(rows) ? rows : [];
  const paper = passthroughMuiProps(rest, {
    omit: ['title', 'columns', 'rows', 'TitleProps', 'tableStyle'],
    defaults: { elevation: 0 },
    overrides: { style: mergeStyles({ overflowX: 'auto', padding: 8 }, p.style) },
  });
  return (
    <Paper {...paper}>
      {title ? (
        <Typography
          {...passthroughMuiProps(TitleProps || {}, {
            defaults: { variant: 'h6' },
            overrides: { style: mergeStyles({ padding: 8 }, TitleProps && TitleProps.style) },
          })}
        >
          {title}
        </Typography>
      ) : null}
      <table
        style={mergeStyles(
          { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
          tableStyle,
        )}
      >
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={c.key}
                style={mergeStyles(
                  {
                    textAlign: 'left',
                    padding: '8px 12px',
                    borderBottom: '2px solid #ddd',
                  },
                  c.headerStyle,
                )}
              >
                {c.label || c.key}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.length === 0 ? (
            <tr>
              <td colSpan={Math.max(cols.length, 1)} style={{ padding: 12, color: '#666' }}>
                No rows
              </td>
            </tr>
          ) : (
            dataRows.map((row, ri) => (
              <tr
                key={row.id || ri}
                onClick={() => onEvent && onEvent('onRowClick', { row })}
                style={{ cursor: onEvent ? 'pointer' : 'default' }}
              >
                {cols.map((c) => (
                  <td
                    key={c.key}
                    style={mergeStyles(
                      { padding: '8px 12px', borderBottom: '1px solid #eee' },
                      c.cellStyle,
                    )}
                  >
                    {row[c.key] != null ? String(row[c.key]) : ''}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </Paper>
  );
}

export function ImageView({ props: p = {} }) {
  if (!p.src) return null;
  const img = passthroughMuiProps(p, {
    defaults: { alt: '' },
    overrides: {
      style: mergeStyles({ maxWidth: '100%', height: 'auto' }, p.style),
    },
  });
  return <img {...img} alt={img.alt || ''} />;
}

export function LinkView({ props: p = {} }) {
  const { text, href, ...rest } = p;
  const mui = passthroughMuiProps(rest, {
    omit: ['text'],
    defaults: { target: '_blank', rel: 'noopener noreferrer' },
    overrides: { href: href || '#' },
  });
  return <MuiLink {...mui}>{text || href}</MuiLink>;
}

function ChartShell({ children, height, style }) {
  return (
    <div style={mergeStyles({ width: '100%', height: height || 280 }, style)}>
      <ResponsiveContainer>{children}</ResponsiveContainer>
    </div>
  );
}

export function LineChartView({ props: p = {} }) {
  const { title, data, xKey, series, height, TitleProps, chartProps, PaperProps, colors, ...rest } = p;
  const rows = Array.isArray(data) ? data : [];
  const seriesList = Array.isArray(series) ? series : [];
  const palette = Array.isArray(colors) ? colors : CHART_COLORS;
  const paper = passthroughMuiProps(PaperProps || rest, {
    omit: ['title', 'data', 'xKey', 'series', 'height', 'TitleProps', 'chartProps', 'colors'],
    defaults: { elevation: 0 },
    overrides: { style: mergeStyles({ padding: 8 }, (PaperProps && PaperProps.style) || p.style) },
  });
  return (
    <Paper {...paper}>
      {title ? (
        <Typography {...passthroughMuiProps(TitleProps || {}, { defaults: { variant: 'subtitle1' } })}>
          {title}
        </Typography>
      ) : null}
      <ChartShell height={height}>
        <LineChart data={rows} {...(chartProps || {})}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xKey || 'x'} />
          <YAxis />
          <Tooltip />
          <Legend />
          {seriesList.map((s, i) => (
            <Line
              key={s.dataKey || i}
              type="monotone"
              dataKey={s.dataKey}
              name={s.name || s.dataKey}
              stroke={s.stroke || palette[i % palette.length]}
              strokeWidth={s.strokeWidth != null ? s.strokeWidth : 2}
              dot={s.dot != null ? s.dot : false}
              {...(s.lineProps || {})}
            />
          ))}
        </LineChart>
      </ChartShell>
    </Paper>
  );
}

export function BarChartView({ props: p = {} }) {
  const { title, data, xKey, series, height, TitleProps, chartProps, PaperProps, colors, ...rest } = p;
  const rows = Array.isArray(data) ? data : [];
  const seriesList = Array.isArray(series) ? series : [];
  const palette = Array.isArray(colors) ? colors : CHART_COLORS;
  const paper = passthroughMuiProps(PaperProps || rest, {
    omit: ['title', 'data', 'xKey', 'series', 'height', 'TitleProps', 'chartProps', 'colors'],
    defaults: { elevation: 0 },
    overrides: { style: mergeStyles({ padding: 8 }, (PaperProps && PaperProps.style) || p.style) },
  });
  return (
    <Paper {...paper}>
      {title ? (
        <Typography {...passthroughMuiProps(TitleProps || {}, { defaults: { variant: 'subtitle1' } })}>
          {title}
        </Typography>
      ) : null}
      <ChartShell height={height}>
        <BarChart data={rows} {...(chartProps || {})}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xKey || 'x'} />
          <YAxis />
          <Tooltip />
          <Legend />
          {seriesList.map((s, i) => (
            <Bar
              key={s.dataKey || i}
              dataKey={s.dataKey}
              name={s.name || s.dataKey}
              fill={s.fill || palette[i % palette.length]}
              {...(s.barProps || {})}
            />
          ))}
        </BarChart>
      </ChartShell>
    </Paper>
  );
}

export function PieChartView({ props: p = {} }) {
  const { title, data, nameKey, valueKey, height, TitleProps, chartProps, PaperProps, colors, ...rest } = p;
  const rows = Array.isArray(data) ? data : [];
  const palette = Array.isArray(colors) ? colors : CHART_COLORS;
  const paper = passthroughMuiProps(PaperProps || rest, {
    omit: ['title', 'data', 'nameKey', 'valueKey', 'height', 'TitleProps', 'chartProps', 'colors'],
    defaults: { elevation: 0 },
    overrides: { style: mergeStyles({ padding: 8 }, (PaperProps && PaperProps.style) || p.style) },
  });
  return (
    <Paper {...paper}>
      {title ? (
        <Typography {...passthroughMuiProps(TitleProps || {}, { defaults: { variant: 'subtitle1' } })}>
          {title}
        </Typography>
      ) : null}
      <ChartShell height={height}>
        <PieChart {...(chartProps || {})}>
          <Pie
            data={rows}
            dataKey={valueKey || 'value'}
            nameKey={nameKey || 'name'}
            cx="50%"
            cy="50%"
            outerRadius={90}
            label
          >
            {rows.map((_, i) => (
              <Cell key={i} fill={palette[i % palette.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ChartShell>
    </Paper>
  );
}

export function FormView({ props: p = {}, children, onEvent }) {
  const mui = passthroughMuiProps(p, {
    omit: ['onSubmit'],
    overrides: {
      style: mergeStyles({ width: '100%' }, p.style),
      onSubmit: (e) => {
        e.preventDefault();
        if (onEvent) onEvent('onSubmit', {});
      },
    },
  });
  return <form {...mui}>{children}</form>;
}

export function TextFieldView({ props: p = {}, onEvent, error }) {
  // Full MUI TextField surface: variant, style, className, classes, InputProps, etc.
  const mui = passthroughMuiProps(p, {
    defaults: {
      fullWidth: true,
      margin: 'normal',
      variant: 'standard',
    },
    overrides: {
      value: p.value != null ? p.value : '',
      error: error ? true : !!p.error,
      helperText: error || p.helperText || '',
      onChange: (e) => {
        if (typeof p.onChange === 'function') p.onChange(e);
        if (onEvent) onEvent('onChange', { value: e.target.value, name: p.name });
      },
    },
  });
  return <TextField {...mui} />;
}

export function SelectView({ props: p = {}, onEvent }) {
  const { options, label, FormControlProps, InputLabelProps, SelectProps, ...rest } = p;
  const opts = Array.isArray(options) ? options : [];
  const formControl = passthroughMuiProps(FormControlProps || {}, {
    defaults: { fullWidth: true, margin: 'normal' },
  });
  const select = passthroughMuiProps(
    { ...rest, ...(SelectProps || {}) },
    {
      omit: ['options', 'label', 'FormControlProps', 'InputLabelProps', 'SelectProps', 'statePath'],
      overrides: {
        value: p.value != null ? p.value : '',
        onChange: (e) => {
          if (SelectProps && typeof SelectProps.onChange === 'function') SelectProps.onChange(e);
          if (onEvent) onEvent('onChange', { value: e.target.value, name: p.name });
        },
      },
    },
  );
  return (
    <FormControl {...formControl}>
      {label ? <InputLabel {...passthroughMuiProps(InputLabelProps || {})}>{label}</InputLabel> : null}
      <Select {...select}>
        {opts.map((opt) => (
          <MenuItem
            key={opt.value != null ? opt.value : String(opt)}
            value={opt.value != null ? opt.value : opt}
            {...passthroughMuiProps(opt.MenuItemProps || {})}
          >
            {opt.label != null ? opt.label : String(opt)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function CheckboxView({ props: p = {}, onEvent }) {
  const { label, FormControlLabelProps, CheckboxProps, ...rest } = p;
  const control = passthroughMuiProps(
    { ...rest, ...(CheckboxProps || {}) },
    {
      omit: ['label', 'FormControlLabelProps', 'CheckboxProps', 'statePath'],
      overrides: {
        checked: !!p.checked,
        onChange: (e) => {
          if (CheckboxProps && typeof CheckboxProps.onChange === 'function') CheckboxProps.onChange(e);
          if (onEvent) {
            onEvent('onChange', {
              checked: e.target.checked,
              value: e.target.checked,
              name: p.name,
            });
          }
        },
      },
    },
  );
  return (
    <FormControlLabel
      {...passthroughMuiProps(FormControlLabelProps || {})}
      control={<Checkbox {...control} />}
      label={label}
    />
  );
}

export function ButtonView({ props: p = {}, onEvent }) {
  const { label, children: labelChildren, ...rest } = p;
  const mui = passthroughMuiProps(rest, {
    omit: ['label'],
    defaults: {
      variant: 'contained',
      color: 'primary',
      type: 'button',
    },
    overrides: {
      style: mergeStyles({ marginTop: 8, marginRight: 8 }, p.style),
      onClick: (e) => {
        if (typeof p.onClick === 'function') p.onClick(e);
        if (onEvent) onEvent('onClick', {});
      },
    },
  });
  return <Button {...mui}>{label != null ? label : (labelChildren || 'Button')}</Button>;
}

export function UnknownComponent({ componentId, nodeId }) {
  return (
    <Box p={2} style={{ border: '1px solid #ffeaa7', background: '#fff3cd', borderRadius: 4 }}>
      <Typography variant="subtitle2">Unknown component</Typography>
      <Typography variant="body2">
        {componentId || '?'} (node {nodeId || '?'})
      </Typography>
    </Box>
  );
}

export function registerBuiltinComponents() {
  const pairs = [
    ['builtin:Stack', StackView],
    ['builtin:Grid', GridView],
    ['builtin:Box', BoxView],
    ['builtin:Divider', DividerView],
    ['builtin:Spacer', SpacerView],
    ['builtin:Typography', TypographyView],
    ['builtin:Banner', BannerView],
    ['builtin:MetricCard', MetricCardView],
    ['builtin:List', ListView],
    ['builtin:DataTable', DataTableView],
    ['builtin:Image', ImageView],
    ['builtin:Link', LinkView],
    ['builtin:LineChart', LineChartView],
    ['builtin:BarChart', BarChartView],
    ['builtin:PieChart', PieChartView],
    ['builtin:Form', FormView],
    ['builtin:TextField', TextFieldView],
    ['builtin:Select', SelectView],
    ['builtin:Checkbox', CheckboxView],
    ['builtin:Button', ButtonView],
  ];
  pairs.forEach(([id, Comp]) => registerReact(id, '1.0.0', Comp));
}
